/* eslint-disable etc/no-commented-out-code */
import { Icon } from "@iconify/react";
import { FormikConfig, FormikProps, useFormik } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { Input } from "../../components/shared/InputField";
import { SwitchButton } from "../../components/shared/SwitchButton";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  Error,
  HStack,
  SettingHeader,
  SettingsRow,
  VStack,
  isEsopViewer,
} from "../../components/utils";
import {
  useGetEmailSettings,
  useSetEmailSettings,
} from "../../queries/siteSettings";
import { usePermissionStore } from "../../store/permissionStore";
import { EmailSetting } from "../../types/SiteSettings";
import { determineUserAccessToResource } from "../../utils/auth";
import {
  Action as DefaultAction,
  Resource,
} from "../../utils/interfaces/Companies";
import Tooltip from "../../components/shared/Tooltip";

type SubSettingsProps = {
  formik: FormikProps<EmailSetting>;
};
type SettingsRowProps = {
  label: string;
  description?: string;
  value: boolean;
};

function EmailSettings({ openCategory }: { openCategory?: string }) {
  const {
    data: emailSettingDetails,
    refetch,
    isError,
    isFetched,
  } = useGetEmailSettings();
  const { mutate: setEmailSettings, isLoading: isSettingsSaved } =
    useSetEmailSettings();
  const { permission } = usePermissionStore();
  const isUserEsopViewer = isEsopViewer();
  const initialValues: EmailSetting = {
    emailsToBeSent: emailSettingDetails?.emailsToBeSent || false,
    approverMailToAdmin: emailSettingDetails?.approverMailToAdmin || false,
    communicationEmailId:
      emailSettingDetails?.communicationEmailId == null
        ? "hissa@rulezero.com"
        : emailSettingDetails?.communicationEmailId || "hissa@rulezero.com",
    senderNameForEmail:
      emailSettingDetails?.senderNameForEmail == null
        ? "Hissa"
        : emailSettingDetails?.senderNameForEmail || "Hissa",
    isAttachmentEnabled: emailSettingDetails?.isAttachmentEnabled ?? false,
    isAttachmentEnabledForAdmins:
      emailSettingDetails?.isAttachmentEnabledForAdmins ?? false,
    reminderEmailsToBeSent:
      emailSettingDetails?.reminderEmailsToBeSent || false,
    reminderEmailsStartFrom: emailSettingDetails?.reminderEmailsStartFrom || 0,
    reminderEmailsInterval: emailSettingDetails?.reminderEmailsInterval || 0,
    employeeInactivityLogout:
      emailSettingDetails?.employeeInactivityLogout || false,
    sendEmailsToEmployees: emailSettingDetails?.sendEmailsToEmployees || false,
    sendGrantEmailsToEmployees:
      emailSettingDetails?.sendGrantEmailsToEmployees || false,
    sendVestingEmailsToEmployees:
      emailSettingDetails?.sendVestingEmailsToEmployees || false,
    sendBuyBackEmailsToEmployees:
      emailSettingDetails?.sendBuyBackEmailsToEmployees || false,
    sendGrantOfferedEmailsToEmployees:
      emailSettingDetails?.sendGrantOfferedEmailsToEmployees || false,
    sendGrantWithdrawEmailsToEmployees:
      emailSettingDetails?.sendGrantWithdrawEmailsToEmployees || false,
    sendGrantReminderEmailsToEmployees:
      emailSettingDetails?.sendGrantReminderEmailsToEmployees || false,
    sendVestingCompleteEmailsToEmployees:
      emailSettingDetails?.sendVestingCompleteEmailsToEmployees || false,
    sendBuyBackOfferedEmailsToEmployees:
      emailSettingDetails?.sendBuyBackOfferedEmailsToEmployees || false,
    sendBuyBackClosedEmailsToEmployees:
      emailSettingDetails?.sendBuyBackClosedEmailsToEmployees || false,
    sendForfeitureLeterToEmployees:
      emailSettingDetails?.sendForfeitureLeterToEmployees || false,
    sendBuyBackReminderEmailsToEmployees:
      emailSettingDetails?.sendBuyBackReminderEmailsToEmployees || false,
    sendFullAndFinalLetterToEmployees:
      emailSettingDetails?.sendFullAndFinalLetterToEmployees || false,
    sendEmailsToAdmin: emailSettingDetails?.sendEmailsToAdmin || false,
    sendGrantEmailsToAdmin:
      emailSettingDetails?.sendGrantEmailsToAdmin || false,
    sendPlanEmailsToAdmin: emailSettingDetails?.sendPlanEmailsToAdmin || false,
    sendTransactionEmailsToAdmin:
      emailSettingDetails?.sendTransactionEmailsToAdmin || false,
    sendReminderEmailsToAdmin:
      emailSettingDetails?.sendReminderEmailsToAdmin || false,
    sendResignationReminderEmailsToAdmin:
      emailSettingDetails?.sendResignationReminderEmailsToAdmin || false,
    sendOptionsExpiryReminderEmailsToEmployee:
      emailSettingDetails?.sendOptionsExpiryReminderEmailsToEmployee || false,
    optionsExpiryReminderStartFromEmployee:
      emailSettingDetails?.optionsExpiryReminderStartFromEmployee || 0,
    optionsExpiryReminderIntervalEmployee:
      emailSettingDetails?.optionsExpiryReminderIntervalEmployee || 0,
    sendEmployeeDataSyncRecords:
      emailSettingDetails?.sendEmployeeDataSyncRecords || false,
    sendGrantAcceptedEmailsToEmployees:
      emailSettingDetails?.sendGrantAcceptedEmailsToEmployees || false,
    sendGrantAcceptedEmailsToAdmin:
      emailSettingDetails?.sendGrantAcceptedEmailsToAdmin || false,
    sendZeroVestingEmailsToEmployees:
      emailSettingDetails?.sendZeroVestingEmailsToEmployees || false,
    sendPendingResignationSummaryEmailsToAdmin:
      emailSettingDetails?.sendPendingResignationSummaryEmailsToAdmin || false,
    pendingResignationInclusionOffset:
      emailSettingDetails?.pendingResignationInclusionOffset || 0,
    pendingResignationReminderInterval:
      emailSettingDetails?.pendingResignationReminderInterval || 0,
    sendOptionsExpiryReminderEmailsToAdmin:
      emailSettingDetails?.sendOptionsExpiryReminderEmailsToAdmin || false,
    optionsExpiryReminderStartFromAdmin:
      emailSettingDetails?.optionsExpiryReminderStartFromAdmin || 0,
    optionsExpiryReminderIntervalAdmin:
      emailSettingDetails?.optionsExpiryReminderIntervalAdmin || 0,
    sendMilestoneCompletionReminderEmailsToAdmin:
      emailSettingDetails?.sendMilestoneCompletionReminderEmailsToAdmin ||
      false,
    sendMonthlyStatement: emailSettingDetails?.sendMonthlyStatement || false,
    monthlyStatementInterval:
      emailSettingDetails?.monthlyStatementInterval || 1,
  };

  const [initialSettings, setInitialSettings] = useState<EmailSetting>();

  const initialSettingsSet = useRef(false);

  useEffect(() => {
    if (emailSettingDetails) {
      formik.setValues({ ...initialValues, ...emailSettingDetails });
      if (!initialSettingsSet.current) {
        setInitialSettings(_.cloneDeep(initialValues));
        initialSettingsSet.current = true;
      }
    }
  }, [emailSettingDetails]);
  const tabs = [
    { name: "General", isDisabled: false },
    { name: "Admin", isDisabled: false },
    { name: "Employee", isDisabled: false },
  ];
  const [handleChanges, setHandleChanges] = useState<boolean>();
  const validationSchema = Yup.object({
    emailsToBeSent: Yup.boolean(),
    communicationEmailId: Yup.string().when(["emailsToBeSent"], {
      is: (emailsToBeSent: boolean) => emailsToBeSent === true,
      then: Yup.string().email().required("Valid Email is required"),
    }),
    senderNameForEmail: Yup.string().when(["emailsToBeSent"], {
      is: (emailsToBeSent: boolean) => emailsToBeSent === true,
      then: Yup.string()
        .required("Email Sender Name is required")
        .min(1, "minimum one character required")
        .max(100),
    }),
    reminderEmailsToBeSent: Yup.boolean().required(),
    reminderEmailsStartFrom: Yup.number().when(["reminderEmailsToBeSent"], {
      is: (reminderEmailsToBeSent: boolean) => reminderEmailsToBeSent === true,
      then: Yup.number()
        .positive("required")
        .required("required")
        .typeError("expected a number"),
    }),
    reminderEmailsInterval: Yup.number().when(["reminderEmailsToBeSent"], {
      is: (reminderEmailsToBeSent: boolean) => reminderEmailsToBeSent === true,
      then: Yup.number()
        .positive("required")
        .required("required")
        .typeError("expected a number"),
    }),
    employeeInactivityLogout: Yup.boolean().required(),
    approverMailToAdmin: Yup.boolean(),
    isAttachmentEnabled: Yup.boolean(),
    sendGrantEmailsToEmployees: Yup.boolean().required(),
    sendVestingEmailsToEmployees: Yup.boolean().required(),
    sendBuyBackEmailsToEmployees: Yup.boolean().required(),
    sendGrantOfferedEmailsToEmployees: Yup.boolean().required(),
    sendGrantWithdrawEmailsToEmployees: Yup.boolean().required(),
    sendGrantReminderEmailsToEmployees: Yup.boolean().required(),
    sendVestingCompleteEmailsToEmployees: Yup.boolean().required(),
    sendBuyBackOfferedEmailsToEmployees: Yup.boolean().required(),
    sendBuyBackClosedEmailsToEmployees: Yup.boolean().required(),
    sendForfeitureLeterToEmployees: Yup.boolean().when(
      ["sendBuyBackClosedEmailsToEmployees"],
      {
        is: (sendBuyBackClosedEmailsToEmployees: boolean) =>
          sendBuyBackClosedEmailsToEmployees === true,
        then: Yup.boolean(),
      }
    ),
    sendBuyBackReminderEmailsToEmployees: Yup.boolean().required(),
    sendFullAndFinalLetterToEmployees: Yup.boolean().required(),
    sendEmailsToAdmin: Yup.boolean().required(),
    sendGrantEmailsToAdmin: Yup.boolean().required(),
    sendPlanEmailsToAdmin: Yup.boolean().required(),
    sendTransactionEmailsToAdmin: Yup.boolean().required(),
    sendReminderEmailsToAdmin: Yup.boolean().required(),
    sendResignationReminderEmailsToAdmin: Yup.boolean(),
    sendOptionsExpiryReminderEmailsToEmployee: Yup.boolean(),
    optionsExpiryReminderStartFromEmployee: Yup.number().when(
      ["sendOptionsExpiryReminderEmailsToEmployee"],
      {
        is: (sendOptionsExpiryReminderEmailsToEmployee: boolean) =>
          sendOptionsExpiryReminderEmailsToEmployee === true,
        then: Yup.number()
          .positive("required")
          .required("required")
          .moreThan(0)
          .typeError("expected a number")
          .label("Start reminders (days before expiry)"),
      }
    ),
    optionsExpiryReminderIntervalEmployee: Yup.number().when(
      ["sendOptionsExpiryReminderEmailsToEmployee"],
      {
        is: (sendOptionsExpiryReminderEmailsToEmployee: boolean) =>
          sendOptionsExpiryReminderEmailsToEmployee === true,
        then: Yup.number()
          .min(1, "Value must be 1 or greater")
          .typeError("expected a number"),
      }
    ),
    sendEmployeeDataSyncRecords: Yup.boolean(),
    sendGrantAcceptedEmailsToEmployees: Yup.boolean(),
    sendMonthlyStatement: Yup.boolean().required(),
    monthlyStatementInterval: Yup.number().when(["sendMonthlyStatement"], {
      is: (sendMonthlyStatement: boolean) => sendMonthlyStatement === true,
      then: Yup.number()
        .min(1, "Minimum interval is 1 month")
        .max(12, "Maximum interval is 12 months")
        .required("required")
        .typeError("expected a number"),
    }),
    sendPendingResignationSummaryEmailsToAdmin: Yup.boolean().required(),
    pendingResignationInclusionOffset: Yup.number().when(
      ["sendPendingResignationSummaryEmailsToAdmin"],
      {
        is: (sendPendingResignationSummaryEmailsToAdmin: boolean) =>
          sendPendingResignationSummaryEmailsToAdmin === true,
        then: Yup.number()
          .positive("required")
          .required("required")
          .typeError("expected a number"),
      }
    ),
    pendingResignationReminderInterval: Yup.number().when(
      ["sendPendingResignationSummaryEmailsToAdmin"],
      {
        is: (sendPendingResignationSummaryEmailsToAdmin: boolean) =>
          sendPendingResignationSummaryEmailsToAdmin === true,
        then: Yup.number()
          .positive("required")
          .required("required")
          .typeError("expected a number"),
      }
    ),
    sendOptionsExpiryReminderEmailsToAdmin: Yup.boolean().required(),
    optionsExpiryReminderStartFromAdmin: Yup.number().when(
      ["sendOptionsExpiryReminderEmailsToAdmin"],
      {
        is: (sendOptionsExpiryReminderEmailsToAdmin: boolean) =>
          sendOptionsExpiryReminderEmailsToAdmin === true,
        then: Yup.number()
          .positive("required")
          .required("required")
          .typeError("expected a number"),
      }
    ),
    optionsExpiryReminderIntervalAdmin: Yup.number().when(
      ["sendOptionsExpiryReminderEmailsToAdmin"],
      {
        is: (sendOptionsExpiryReminderEmailsToAdmin: boolean) =>
          sendOptionsExpiryReminderEmailsToAdmin === true,
        then: Yup.number()
          .positive("required")
          .required("required")
          .typeError("expected a number"),
      }
    ),
  });

  function handleOnCancel(values: EmailSetting) {
    values = initialValues;
  }
  const handleSubmit = (values: EmailSetting) => {
    // fix later to avoid any
    const changedValues: any = {};
    Object.keys(values).forEach((key) => {
      const originalKey: keyof EmailSetting = key as keyof EmailSetting;
      if (values[originalKey] !== initialValues[originalKey]) {
        changedValues[originalKey] = values[originalKey];
      }
    });
    setEmailSettings(changedValues, {
      onSuccess: () => {
        toast("Saved Successfully!", {
          type: "success",
          autoClose: 2000,
        });
      },
      onError: (err: any) => {
        toast(err.response.data.errorRaw, { type: "error", autoClose: 2000 });
      },
    });
  };
  const formik = useFormik<EmailSetting>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: (values) => {
      handleSubmit(values);
    },
    onReset: (values) => {
      handleOnCancel(values);
    },
  });
  const allTabs = useMemo(() => {
    const isTabDisabled = (tabName: string) => {
      tabName = tabName.toLowerCase();
      if (tabName === "general") return false;
      if (
        formik.values.emailsToBeSent === false &&
        (tabName === "admin" || tabName === "employee")
      )
        return true;
      return false;
    };
    return tabs.map((tab) => ({ ...tab, isDisabled: isTabDisabled(tab.name) }));
  }, [formik.values.emailsToBeSent]);
  const [activeTab, setActiveTab] = useState<{
    name: string;
    isDisabled: boolean;
  }>(allTabs[0]);

  useEffect(() => {
    if (openCategory) {
      const found = allTabs.find(
        (t) => t.name.toLowerCase() === openCategory.toLowerCase()
      );
      if (found && !found.isDisabled) setActiveTab(found);
    }
  }, [openCategory]);

  useEffect(() => {
    const hasChanges = _.isEqual(emailSettingDetails, formik.values);

    setHandleChanges(hasChanges);
  }, [initialSettings, formik.values]);

  const { values, errors, touched, getFieldProps, setFieldValue } = formik;
  const emailSettingsErrors =
    errors.communicationEmailId ||
    errors.senderNameForEmail ||
    errors.emailsToBeSent ||
    errors.reminderEmailsToBeSent ||
    errors.reminderEmailsStartFrom ||
    errors.reminderEmailsInterval ||
    errors.employeeInactivityLogout ||
    errors.approverMailToAdmin ||
    errors.isAttachmentEnabled;
  function doPartialTouch() {
    formik.setFieldTouched("communicationEmailId");
    formik.setFieldTouched("senderNameForEmail");
    formik.setFieldTouched("emailsToBeSent");
    formik.setFieldTouched("reminderEmailsToBeSent");
    formik.setFieldTouched("reminderEmailsStartFrom");
    formik.setFieldTouched("reminderEmailsInterval");
    formik.setFieldTouched("employeeInactivityLogout");
    formik.setFieldTouched("approverMailToAdmin");
    formik.setFieldTouched("isAttachmentEnabled");
  }

  return (
    <form key="emailSettings" onSubmit={formik.handleSubmit}>
      <Box className="p-4 bg-white rounded-lg w-full">
        <Box className="">
          <VStack className="justify-between p-4 gap-4">
            <Box className="items-center mt-6  text-gray-dark">
              <SettingHeader
                title="Email Settings"
                description="Configure your email settings"
              />
            </Box>
            <HStack className="gap-8 mb-0 border-t-2 border-dotted flex-center">
              {allTabs.map((tab) => (
                <Box
                  onClick={() => {
                    if (!tab.isDisabled) setActiveTab(tab);
                  }}
                  key={tab.name}
                  className={`text-xs3 font-semibold py-4  ${
                    tab.isDisabled
                      ? "text-blue-400 cursor-not-allowed"
                      : "cursor-pointer"
                  } ${
                    tab.name === activeTab.name
                      ? "border-b-2 border-solid border-red-500 text-red-500"
                      : "text-gray-201 "
                  }`}
                >
                  {tab.name}
                </Box>
              ))}
            </HStack>
            <VStack className="max-h-[450px] overflow-auto p-4 w-200">
              {activeTab.name === "General" && (
                <GeneralEmailSettings formik={formik} />
              )}
              {activeTab.name === "Admin" && (
                <AdminEmailSettings formik={formik} />
              )}
              {activeTab.name === "Employee" && (
                <EmployeeEmailSettings formik={formik} />
              )}
            </VStack>
          </VStack>
        </Box>

        {!isUserEsopViewer && (
          <HStack className="justify-between mt-12">
            <ButtonPrimary1 type="reset" className="mr-8 text-red-500">
              Cancel
            </ButtonPrimary1>
            <ButtonPrimary
              className={`${
                !handleChanges ||
                !determineUserAccessToResource(
                  permission?.aclList || [],
                  Resource.CompanySettings,
                  DefaultAction.All
                )
                  ? " cursor-not-allowed"
                  : ""
              } flex items-center self-end justify-center`}
              disabled={
                isError ||
                handleChanges ||
                !determineUserAccessToResource(
                  permission?.aclList || [],
                  Resource.CompanySettings,
                  DefaultAction.All
                )
              }
              type="submit"
              loading={isSettingsSaved}
            >
              Save Settings
            </ButtonPrimary>
          </HStack>
        )}
      </Box>
    </form>
  );
}

const GeneralEmailSettings: React.FC<SubSettingsProps> = ({ formik }) => {
  const { getFieldProps, values, touched, errors } = formik;
  return (
    <>
      <HStack className="gap-10 pb-6">
        <VStack className="w-full gap-10">
          <SettingsRow
            id="email-emailsToBeSent"
            label="Allow Notification Emails"
            description="Allow hissa to send notification emails from the platform, this acts a flag to turn on/off all types of notification emails."
            name="emailsToBeSent"
            type="toggle"
            value={formik.values.emailsToBeSent}
            onToggle={() => {
              formik.setFieldValue(
                "emailsToBeSent",
                !formik.values.emailsToBeSent
              );
            }}
          />
          <SettingsRow
            id="email-communicationEmailId"
            label="Email Sender Address"
            description={`The email address that will be used to send emails, an invalid email will cause the emails to bounce.`}
            type="input"
            inputType="email"
            value={values.communicationEmailId}
            name="communicationEmailId"
            getFieldProps={getFieldProps}
            error={errors?.communicationEmailId}
            // error={touched?.communicationEmailId && errors?.communicationEmailId}
            disabled={!values.emailsToBeSent}
          />
          <SettingsRow
            id="email-senderNameForEmail"
            label="Email Sender Name"
            description={`The name that will be used to send emails`}
            type="input"
            inputType="text"
            value={values.senderNameForEmail}
            name="senderNameForEmail"
            getFieldProps={getFieldProps}
            error={errors?.senderNameForEmail}
            // error={touched?.senderNameForEmail && errors?.senderNameForEmail}
            disabled={!values.emailsToBeSent}
          />
          <SettingsRow
            id="email-employeeInactivityLogout"
            label="Allow admin to deactivate inactive Employees Accounts"
            description={`Allow admin to deactivate inactive Employees Accounts`}
            name="employeeInactivityLogout"
            getFieldProps={getFieldProps}
            value={formik.values.employeeInactivityLogout}
            onToggle={() => {
              formik.setFieldValue(
                "employeeInactivityLogout",
                !formik.values.employeeInactivityLogout
              );
            }}
            error={errors?.employeeInactivityLogout}
            type="toggle"
          />
          <SettingsRow
            id="email-reminderEmailsToBeSent"
            label="Reminder Emails"
            name="reminderEmailsToBeSent"
            description={`Allow hissa to send reminder emails`}
            getFieldProps={getFieldProps}
            value={formik.values.reminderEmailsToBeSent}
            onToggle={() => {
              formik.setFieldValue(
                "reminderEmailsToBeSent",
                !formik.values.reminderEmailsToBeSent
              );
            }}
            error={
              formik.touched.reminderEmailsToBeSent
                ? errors?.reminderEmailsToBeSent
                : undefined
            }
            type="toggle"
          />
          <VStack
            className={`gap-4 pl-10 ${
              formik.values.reminderEmailsToBeSent
                ? ""
                : "opacity-50 cursor-not-allowed"
            }`}
          >
            <SettingsRow
              id="email-reminderEmailsStartFrom"
              label="Start reminder Emails after"
              value={formik.values.reminderEmailsStartFrom}
              getFieldProps={getFieldProps}
              error={
                formik.touched.reminderEmailsStartFrom
                  ? errors?.reminderEmailsStartFrom
                  : undefined
              }
              name="reminderEmailsStartFrom"
              description={`Number of days after which the reminder emails should start`}
              type="input"
              inputType="number"
              disabled={!formik.values.reminderEmailsToBeSent}
            />
            <SettingsRow
              id="email-reminderEmailsInterval"
              label="Set reminder Emails interval to"
              value={formik.values.reminderEmailsInterval}
              getFieldProps={getFieldProps}
              error={
                formik.touched.reminderEmailsInterval
                  ? errors?.reminderEmailsInterval
                  : undefined
              }
              name="reminderEmailsInterval"
              description={`How frequently the reminder emails should be sent (in days)`}
              type="input"
              inputType="number"
              disabled={!formik.values.reminderEmailsToBeSent}
            />
          </VStack>
        </VStack>
      </HStack>
    </>
  );
};
const AdminEmailSettings: React.FC<SubSettingsProps> = ({ formik }) => {
  const { getFieldProps, values, touched, errors } = formik;
  return (
    <>
      <HStack className="pb-6">
        <VStack className=" gap-10">
          <SettingsRow
            id="email-sendEmailsToAdmin"
            label="Allow Hissa to send notification Emails to Admins"
            description="Allow Hissa to send notification Emails to Admins, this also controls if any emails are sent to admins."
            name="sendEmailsToAdmin"
            type="toggle"
            getFieldProps={getFieldProps}
            value={formik.values.sendEmailsToAdmin}
            onToggle={() => {
              formik.setFieldValue(
                "sendEmailsToAdmin",
                !formik.values.sendEmailsToAdmin
              );
            }}
          />
          <VStack
            className={`justify-between gap-5 pl-10 ${
              formik.values.sendEmailsToAdmin
                ? ""
                : "opacity-50 cursor-not-allowed"
            }`}
          >
            <SettingsRow
              id="email-approverMailToAdmin"
              label="Allow admin to receive all approver Emails"
              description="Enable this option to allow admins to receive all approver emails."
              value={formik.values.approverMailToAdmin}
              getFieldProps={getFieldProps}
              type="toggle"
              onToggle={() => {
                formik.setFieldValue(
                  "approverMailToAdmin",
                  !formik.values.approverMailToAdmin
                );
              }}
              name="approverMailToAdmin"
              disabled={!formik.values.sendEmailsToAdmin}
            />
            <SettingsRow
              id="email-sendGrantEmailsToAdmin"
              label="Grant related Emails"
              description="Enable this option to allow Hissa to send grant related emails to admins."
              name="sendGrantEmailsToAdmin"
              type="toggle"
              getFieldProps={getFieldProps}
              value={formik.values.sendGrantEmailsToAdmin}
              onToggle={() => {
                formik.setFieldValue(
                  "sendGrantEmailsToAdmin",
                  !formik.values.sendGrantEmailsToAdmin
                );
              }}
              disabled={!formik.values.sendEmailsToAdmin}
            />
            <VStack
              className={`justify-between gap-5 pl-10 ${
                formik.values.sendGrantEmailsToAdmin
                  ? ""
                  : "opacity-50 cursor-not-allowed"
              }`}
            >
              <SettingsRow
                id="email-isAttachmentEnabledForAdmins"
                label="Allow Admins to receive attachments in the Approval Emails"
                description="Enable this option to allow admins to receive attachments in the approval emails."
                name="isAttachmentEnabledForAdmins"
                type="toggle"
                disabled={
                  !formik.values.sendGrantEmailsToAdmin ||
                  !formik.values.sendEmailsToAdmin
                }
                getFieldProps={getFieldProps}
                value={formik.values.isAttachmentEnabledForAdmins}
                onToggle={() => {
                  formik.setFieldValue(
                    "isAttachmentEnabledForAdmins",
                    !formik.values.isAttachmentEnabledForAdmins
                  );
                }}
              />
              <SettingsRow
                id="email-sendGrantAcceptedEmailsToAdmin"
                label="Allow Admins to get signed grant attached emails"
                description="Enable this option to allow admins to get signed grant attached emails"
                name="sendGrantAcceptedEmailsToAdmin"
                type="toggle"
                getFieldProps={getFieldProps}
                value={formik.values.sendGrantAcceptedEmailsToAdmin}
                onToggle={() => {
                  formik.setFieldValue(
                    "sendGrantAcceptedEmailsToAdmin",
                    !formik.values.sendGrantAcceptedEmailsToAdmin
                  );
                }}
                disabled={
                  !formik.values.sendGrantEmailsToAdmin ||
                  !formik.values.sendEmailsToAdmin
                }
              />
            </VStack>
            <SettingsRow
              id="email-sendPlanEmailsToAdmin"
              label="Plan related Emails"
              description="Enable this option to allow Hissa to send plan related emails to admins."
              name="sendPlanEmailsToAdmin"
              type="toggle"
              getFieldProps={getFieldProps}
              value={formik.values.sendPlanEmailsToAdmin!}
              onToggle={() => {
                formik.setFieldValue(
                  "sendPlanEmailsToAdmin",
                  !formik.values.sendPlanEmailsToAdmin
                );
              }}
              disabled={!formik.values.sendEmailsToAdmin}
            />
            <SettingsRow
              id="email-sendReminderEmailsToAdmin"
              label="Reminder Emails"
              description="Enable this option to allow Hissa to send reminder emails to admins."
              name="sendReminderEmailsToAdmin"
              type="toggle"
              getFieldProps={getFieldProps}
              value={formik.values.sendReminderEmailsToAdmin!}
              onToggle={() => {
                formik.setFieldValue(
                  "sendReminderEmailsToAdmin",
                  !formik.values.sendReminderEmailsToAdmin
                );
              }}
              disabled={!formik.values.sendEmailsToAdmin}
            />
            <SettingsRow
              id="email-milestoneCompletionReminderEmailsToAdmin"
              label="Milestone Completion Reminder Emails"
              description="Enable this option to allow Hissa to send milestone completion reminder emails to admins."
              name="sendMilestoneCompletionReminderEmailsToAdmin"
              type="toggle"
              getFieldProps={getFieldProps}
              value={
                formik.values.sendMilestoneCompletionReminderEmailsToAdmin!
              }
              onToggle={() => {
                formik.setFieldValue(
                  "sendMilestoneCompletionReminderEmailsToAdmin",
                  !formik.values.sendMilestoneCompletionReminderEmailsToAdmin
                );
              }}
              disabled={!formik.values.sendEmailsToAdmin}
            />
            <SettingsRow
              id="email-sendTransactionEmailsToAdmin"
              label="Send Transaction related Emails"
              description={`Send Buyback/Exercise/Liquidity emails to admins.`}
              name="sendTransactionEmailsToAdmin"
              type="toggle"
              getFieldProps={getFieldProps}
              value={formik.values.sendTransactionEmailsToAdmin!}
              onToggle={() => {
                formik.setFieldValue(
                  "sendTransactionEmailsToAdmin",
                  !formik.values.sendTransactionEmailsToAdmin
                );
              }}
              disabled={!formik.values.sendEmailsToAdmin}
            />
            <SettingsRow
              id="email-sendResignationReminderEmailsToAdmin"
              label="Upcoming Resignation Reminder Emails"
              description="Enable this option to allow Hissa to send individual resignation reminder emails to admins one day before an employee's last working day"
              name="sendResignationReminderEmailsToAdmin"
              type="toggle"
              getFieldProps={getFieldProps}
              value={formik.values.sendResignationReminderEmailsToAdmin!}
              onToggle={() => {
                formik.setFieldValue(
                  "sendResignationReminderEmailsToAdmin",
                  !formik.values.sendResignationReminderEmailsToAdmin
                );
              }}
              disabled={!formik.values.sendEmailsToAdmin}
            />
            <SettingsRow
              id="email-sendPendingResignationSummaryEmailsToAdmin"
              label="Pending Resignation Summary Emails"
              name="sendPendingResignationSummaryEmailsToAdmin"
              description={`Send periodic summary emails listing employees whose last working date has passed but whose resignation is still incomplete.`}
              getFieldProps={getFieldProps}
              value={formik.values.sendPendingResignationSummaryEmailsToAdmin}
              onToggle={() => {
                formik.setFieldValue(
                  "sendPendingResignationSummaryEmailsToAdmin",
                  !formik.values.sendPendingResignationSummaryEmailsToAdmin
                );
              }}
              error={
                formik.touched.sendPendingResignationSummaryEmailsToAdmin
                  ? errors?.sendPendingResignationSummaryEmailsToAdmin
                  : undefined
              }
              type="toggle"
            />
            <VStack
              className={`gap-4 pl-10 ${
                formik.values.sendPendingResignationSummaryEmailsToAdmin
                  ? ""
                  : "opacity-50 cursor-not-allowed"
              }`}
            >
              <SettingsRow
                id="email-pendingResignationInclusionOffset"
                label="Include Employees After (Days from Last Working Date)"
                value={formik.values.pendingResignationInclusionOffset}
                getFieldProps={getFieldProps}
                error={
                  formik.touched.pendingResignationInclusionOffset
                    ? errors?.pendingResignationInclusionOffset
                    : undefined
                }
                name="pendingResignationInclusionOffset"
                description={`Number of days after an employee’s last working date before they are included in pending resignation list.`}
                type="input"
                inputType="number"
                disabled={
                  !formik.values.sendPendingResignationSummaryEmailsToAdmin
                }
              />
              <SettingsRow
                id="email-pendingResignationReminderInterval"
                label="Reminder Frequency"
                value={formik.values.pendingResignationReminderInterval}
                getFieldProps={getFieldProps}
                error={
                  formik.touched.pendingResignationReminderInterval
                    ? errors?.pendingResignationReminderInterval
                    : undefined
                }
                name="pendingResignationReminderInterval"
                description={`How frequently the summary emails should be sent (in days)`}
                type="input"
                inputType="number"
                disabled={
                  !formik.values.sendPendingResignationSummaryEmailsToAdmin
                }
              />
            </VStack>
            <SettingsRow
              id="email-sendOptionsExpiryReminderEmailsToAdmin"
              label="Options Expiry Reminder Emails"
              name="sendOptionsExpiryReminderEmailsToAdmin"
              description={`Send periodic reminder emails to admins for grants nearing options expiry.`}
              getFieldProps={getFieldProps}
              value={formik.values.sendOptionsExpiryReminderEmailsToAdmin}
              onToggle={() => {
                formik.setFieldValue(
                  "sendOptionsExpiryReminderEmailsToAdmin",
                  !formik.values.sendOptionsExpiryReminderEmailsToAdmin
                );
              }}
              error={
                formik.touched.sendOptionsExpiryReminderEmailsToAdmin
                  ? errors?.sendOptionsExpiryReminderEmailsToAdmin
                  : undefined
              }
              type="toggle"
              disabled={!formik.values.sendEmailsToAdmin}
            />
            <VStack
              className={`gap-4 pl-10 ${
                formik.values.sendOptionsExpiryReminderEmailsToAdmin
                  ? ""
                  : "opacity-50 cursor-not-allowed"
              }`}
            >
              <SettingsRow
                id="email-optionsExpiryReminderStartFromAdmin"
                label="Start Reminders (Days Before Expiry)"
                value={formik.values.optionsExpiryReminderStartFromAdmin}
                getFieldProps={getFieldProps}
                error={
                  formik.touched.optionsExpiryReminderStartFromAdmin
                    ? errors?.optionsExpiryReminderStartFromAdmin
                    : undefined
                }
                name="optionsExpiryReminderStartFromAdmin"
                description={`Number of days before expiry date when reminders should start`}
                type="input"
                inputType="number"
                disabled={
                  !formik.values.sendOptionsExpiryReminderEmailsToAdmin ||
                  !formik.values.sendEmailsToAdmin
                }
              />
              <SettingsRow
                id="email-optionsExpiryReminderIntervalAdmin"
                label="Reminder Interval"
                value={formik.values.optionsExpiryReminderIntervalAdmin}
                getFieldProps={getFieldProps}
                error={
                  formik.touched.optionsExpiryReminderIntervalAdmin
                    ? errors?.optionsExpiryReminderIntervalAdmin
                    : undefined
                }
                name="optionsExpiryReminderIntervalAdmin"
                description={`How frequently the reminder emails should be sent (in days).`}
                type="input"
                inputType="number"
                disabled={
                  !formik.values.sendOptionsExpiryReminderEmailsToAdmin ||
                  !formik.values.sendEmailsToAdmin
                }
              />
            </VStack>
            <SettingsRow
              id="email-sendEmployeeDataSyncRecords"
              label="Send Bi-Monthly Employee Data Sync History Details to Admins"
              description={`Send bi-monthly employee data sync history details to admins.`}
              name="sendEmployeeDataSyncRecords"
              type="toggle"
              getFieldProps={getFieldProps}
              value={formik.values.sendEmployeeDataSyncRecords!}
              onToggle={() => {
                formik.setFieldValue(
                  "sendEmployeeDataSyncRecords",
                  !formik.values.sendEmployeeDataSyncRecords
                );
              }}
              disabled={!formik.values.sendEmailsToAdmin}
            />
            {/* Monthly statement */}
            <SettingsRow
              id="email-sendMonthlyStatement"
              label="Monthly Statement Emails"
              description="Send periodic portfolio statement emails to admins."
              name="sendMonthlyStatement"
              type="toggle"
              getFieldProps={getFieldProps}
              value={formik.values.sendMonthlyStatement ?? false}
              onToggle={() =>
                formik.setFieldValue(
                  "sendMonthlyStatement",
                  !formik.values.sendMonthlyStatement
                )
              }
              disabled={!formik.values.sendEmailsToAdmin}
            />

            <VStack
              className={`gap-4 pl-10 ${
                formik.values.sendMonthlyStatement
                  ? ""
                  : "opacity-50 cursor-not-allowed"
              }`}
            >
              <SettingsRow
                id="email-monthlyStatementInterval"
                label="Statement Interval"
                description="Number of months each statement covers (1–12 months)."
                value={formik.values.monthlyStatementInterval ?? 1}
                getFieldProps={getFieldProps}
                error={
                  formik.touched.monthlyStatementInterval
                    ? (errors as any)?.monthlyStatementInterval
                    : undefined
                }
                name="monthlyStatementInterval"
                type="input"
                inputType="number"
                disabled={
                  !formik.values.sendEmailsToAdmin ||
                  !formik.values.sendMonthlyStatement
                }
              />
            </VStack>
          </VStack>
        </VStack>
      </HStack>
    </>
  );
};

export const EmployeeEmailSettings: React.FC<SubSettingsProps> = ({
  formik,
}) => {
  const { getFieldProps, values, touched, errors } = formik;

  const disabledIfParentOff = (parent: boolean, child?: boolean) =>
    !values.sendEmailsToEmployees || !parent || !child;

  return (
    <>
      <HStack className="gap-10 pb-6">
        <VStack className="w-full gap-10">
          {/* Root toggle */}
          <SettingsRow
            id="email-sendEmailsToEmployees"
            label="Allow Hissa to send notification Emails to Employees"
            description="This master toggle controls whether any notification emails are sent to employees."
            name="sendEmailsToEmployees"
            type="toggle"
            getFieldProps={getFieldProps}
            value={values.sendEmailsToEmployees}
            onToggle={() =>
              formik.setFieldValue(
                "sendEmailsToEmployees",
                !values.sendEmailsToEmployees
              )
            }
          />

          <VStack
            className={`justify-between gap-5 pl-10 ${
              values.sendEmailsToEmployees
                ? ""
                : "opacity-50 cursor-not-allowed"
            }`}
          >
            {/* Grant related emails */}
            <SettingsRow
              id="email-sendGrantEmailsToEmployees"
              label="Grant related Emails"
              description="Enable grant lifecycle emails for employees."
              name="sendGrantEmailsToEmployees"
              type="toggle"
              getFieldProps={getFieldProps}
              value={values.sendGrantEmailsToEmployees}
              onToggle={() =>
                formik.setFieldValue(
                  "sendGrantEmailsToEmployees",
                  !values.sendGrantEmailsToEmployees
                )
              }
              disabled={!values.sendEmailsToEmployees}
            />

            <VStack
              className={`justify-between gap-5 pl-10 ${
                values.sendGrantEmailsToEmployees
                  ? ""
                  : "opacity-50 cursor-not-allowed"
              }`}
            >
              <SettingsRow
                id="email-sendGrantOfferedEmailsToEmployees"
                label="Offered Emails"
                description="Send emails when grants are offered."
                name="sendGrantOfferedEmailsToEmployees"
                type="toggle"
                getFieldProps={getFieldProps}
                value={values.sendGrantOfferedEmailsToEmployees}
                onToggle={() =>
                  formik.setFieldValue(
                    "sendGrantOfferedEmailsToEmployees",
                    !values.sendGrantOfferedEmailsToEmployees
                  )
                }
                disabled={
                  !values.sendEmailsToEmployees ||
                  !values.sendGrantEmailsToEmployees
                }
              />

              <SettingsRow
                id="email-isAttachmentEnabled"
                label="Offer email attachments"
                description="Allow Employees to receive attachments in the offer letter"
                name="isAttachmentEnabled"
                type="toggle"
                getFieldProps={getFieldProps}
                value={values.isAttachmentEnabled}
                onToggle={() =>
                  formik.setFieldValue(
                    "isAttachmentEnabled",
                    !values.isAttachmentEnabled
                  )
                }
                disabled={
                  !values.sendEmailsToEmployees ||
                  !values.sendGrantEmailsToEmployees
                }
              />

              <SettingsRow
                id="email-sendGrantWithdrawEmailsToEmployees"
                label="Withdrawn Emails"
                description="Send emails when grant offers are withdrawn."
                name="sendGrantWithdrawEmailsToEmployees"
                type="toggle"
                getFieldProps={getFieldProps}
                value={values.sendGrantWithdrawEmailsToEmployees}
                onToggle={() =>
                  formik.setFieldValue(
                    "sendGrantWithdrawEmailsToEmployees",
                    !values.sendGrantWithdrawEmailsToEmployees
                  )
                }
                disabled={
                  !values.sendEmailsToEmployees ||
                  !values.sendGrantEmailsToEmployees
                }
              />

              <SettingsRow
                id="email-sendGrantReminderEmailsToEmployees"
                label="Reminder Emails"
                description="Send grant-related reminders."
                name="sendGrantReminderEmailsToEmployees"
                type="toggle"
                getFieldProps={getFieldProps}
                value={values.sendGrantReminderEmailsToEmployees}
                onToggle={() =>
                  formik.setFieldValue(
                    "sendGrantReminderEmailsToEmployees",
                    !values.sendGrantReminderEmailsToEmployees
                  )
                }
                disabled={
                  !values.sendEmailsToEmployees ||
                  !values.sendGrantEmailsToEmployees
                }
              />

              <SettingsRow
                id="email-sendOptionsExpiryReminderEmailsToEmployee"
                label="Options Expiry Reminder Emails (Employees)"
                description="Send periodic reminder emails to employees for grants nearing options expiry."
                name="sendOptionsExpiryReminderEmailsToEmployee"
                type="toggle"
                getFieldProps={getFieldProps}
                value={values.sendOptionsExpiryReminderEmailsToEmployee}
                onToggle={() =>
                  formik.setFieldValue(
                    "sendOptionsExpiryReminderEmailsToEmployee",
                    !values.sendOptionsExpiryReminderEmailsToEmployee
                  )
                }
                disabled={
                  !values.sendEmailsToEmployees ||
                  !values.sendGrantEmailsToEmployees
                }
              />

              <VStack
                className={`gap-15 pl-10 ${
                  values.sendOptionsExpiryReminderEmailsToEmployee
                    ? ""
                    : "opacity-50 cursor-not-allowed"
                }`}
              >
                <HStack className="items-center justify-between gap-4">
                  <HStack className="items-center gap-2">
                    <VStack>
                      <Input
                        type="text"
                        placeholder="No. of days"
                        className="m-1 w-26"
                        {...getFieldProps(
                          "optionsExpiryReminderStartFromEmployee"
                        )}
                        disabled={
                          !values.sendEmailsToEmployees ||
                          !values.sendGrantEmailsToEmployees ||
                          !values.sendOptionsExpiryReminderEmailsToEmployee
                        }
                      />
                      {touched?.optionsExpiryReminderStartFromEmployee &&
                      (errors as any)
                        ?.optionsExpiryReminderStartFromEmployee ? (
                        <Error
                          text={
                            (errors as any)
                              ?.optionsExpiryReminderStartFromEmployee
                          }
                        />
                      ) : (
                        <p className="text-[9px] text-red-500 my-0.5">&nbsp;</p>
                      )}
                    </VStack>
                    <p className="text-sm font-medium form-label">
                      Days before expiry
                    </p>
                  </HStack>

                  <HStack className="items-center gap-2">
                    <VStack>
                      <Input
                        type="text"
                        placeholder="No. of days"
                        className="m-1 w-26"
                        {...getFieldProps(
                          "optionsExpiryReminderIntervalEmployee"
                        )}
                        disabled={
                          !values.sendEmailsToEmployees ||
                          !values.sendGrantEmailsToEmployees ||
                          !values.sendOptionsExpiryReminderEmailsToEmployee
                        }
                      />
                      {touched?.optionsExpiryReminderIntervalEmployee &&
                      (errors as any)?.optionsExpiryReminderIntervalEmployee ? (
                        <Error
                          text={
                            (errors as any)
                              ?.optionsExpiryReminderIntervalEmployee
                          }
                        />
                      ) : (
                        <p className="text-[9px] text-red-500 my-0.5">&nbsp;</p>
                      )}
                    </VStack>
                    <p className="text-sm font-medium form-label">
                      Interval Days
                    </p>
                  </HStack>
                </HStack>
              </VStack>

              <SettingsRow
                id="email-sendGrantAcceptedEmailsToEmployees"
                label="Grant accepted emails with signed grant attached"
                description="Send email with signed grant attached when employee accepts the grant"
                name="sendGrantAcceptedEmailsToEmployees"
                type="toggle"
                getFieldProps={getFieldProps}
                value={values.sendGrantAcceptedEmailsToEmployees}
                onToggle={() =>
                  formik.setFieldValue(
                    "sendGrantAcceptedEmailsToEmployees",
                    !values.sendGrantAcceptedEmailsToEmployees
                  )
                }
                disabled={
                  !values.sendEmailsToEmployees ||
                  !values.sendGrantEmailsToEmployees
                }
              />
            </VStack>

            {/* Vesting related emails */}
            <SettingsRow
              id="email-sendVestingEmailsToEmployees"
              label="Vesting related Emails to Employees"
              description="Enable vesting updates for employees."
              name="sendVestingEmailsToEmployees"
              type="toggle"
              getFieldProps={getFieldProps}
              value={values.sendVestingEmailsToEmployees}
              onToggle={() =>
                formik.setFieldValue(
                  "sendVestingEmailsToEmployees",
                  !values.sendVestingEmailsToEmployees
                )
              }
              disabled={!values.sendEmailsToEmployees}
            />

            <VStack
              className={`justify-between gap-5 pl-10 ${
                values.sendVestingEmailsToEmployees
                  ? ""
                  : "opacity-50 cursor-not-allowed"
              }`}
            >
              <SettingsRow
                id="email-sendVestingCompleteEmailsToEmployees"
                label="Vesting Completion Emails"
                description="Notify employees when a vesting schedule completes."
                name="sendVestingCompleteEmailsToEmployees"
                type="toggle"
                getFieldProps={getFieldProps}
                value={values.sendVestingCompleteEmailsToEmployees}
                onToggle={() =>
                  formik.setFieldValue(
                    "sendVestingCompleteEmailsToEmployees",
                    !values.sendVestingCompleteEmailsToEmployees
                  )
                }
                disabled={
                  !values.sendEmailsToEmployees ||
                  !values.sendVestingEmailsToEmployees
                }
              />
              <VStack
                className={`justify-between gap-5 pl-10 ${
                  values.sendVestingCompleteEmailsToEmployees
                    ? ""
                    : "opacity-50 cursor-not-allowed"
                }`}
              >
                <SettingsRow
                  id="email-sendZeroVestingEmailsToEmployees"
                  label="Zero Vesting Emails"
                  description="Notify employees even when their grants have zero vesting."
                  name="sendZeroVestingEmailsToEmployees"
                  type="toggle"
                  getFieldProps={getFieldProps}
                  value={values.sendZeroVestingEmailsToEmployees ?? false}
                  onToggle={() =>
                    formik.setFieldValue(
                      "sendZeroVestingEmailsToEmployees",
                      !values.sendZeroVestingEmailsToEmployees
                    )
                  }
                  disabled={
                    !values.sendEmailsToEmployees ||
                    !values.sendVestingEmailsToEmployees ||
                    !values.sendVestingCompleteEmailsToEmployees
                  }
                />
              </VStack>
            </VStack>
            {/* Buyback related emails */}
            <SettingsRow
              id="email-sendBuyBackEmailsToEmployees"
              label="Buyback related Emails"
              description="Enable buyback lifecycle emails for employees."
              name="sendBuyBackEmailsToEmployees"
              type="toggle"
              getFieldProps={getFieldProps}
              value={values.sendBuyBackEmailsToEmployees}
              onToggle={() =>
                formik.setFieldValue(
                  "sendBuyBackEmailsToEmployees",
                  !values.sendBuyBackEmailsToEmployees
                )
              }
              disabled={!values.sendEmailsToEmployees}
            />

            <VStack
              className={`justify-between gap-5 pl-10 ${
                values.sendBuyBackEmailsToEmployees
                  ? ""
                  : "opacity-50 cursor-not-allowed"
              }`}
            >
              <SettingsRow
                id="email-sendBuyBackOfferedEmailsToEmployees"
                label="Offered Emails"
                description="Send emails when buyback offers are available."
                name="sendBuyBackOfferedEmailsToEmployees"
                type="toggle"
                getFieldProps={getFieldProps}
                value={values.sendBuyBackOfferedEmailsToEmployees}
                onToggle={() =>
                  formik.setFieldValue(
                    "sendBuyBackOfferedEmailsToEmployees",
                    !values.sendBuyBackOfferedEmailsToEmployees
                  )
                }
                disabled={
                  !values.sendEmailsToEmployees ||
                  !values.sendBuyBackEmailsToEmployees
                }
              />

              <SettingsRow
                id="email-sendBuyBackClosedEmailsToEmployees"
                label="Closed or Completion Emails to Employees"
                description="Notify employees when a buyback closes or completes."
                name="sendBuyBackClosedEmailsToEmployees"
                type="toggle"
                getFieldProps={getFieldProps}
                value={values.sendBuyBackClosedEmailsToEmployees}
                onToggle={() =>
                  formik.setFieldValue(
                    "sendBuyBackClosedEmailsToEmployees",
                    !values.sendBuyBackClosedEmailsToEmployees
                  )
                }
                disabled={
                  !values.sendEmailsToEmployees ||
                  !values.sendBuyBackEmailsToEmployees
                }
              />

              <VStack
                className={`justify-between pl-10 ${
                  values.sendBuyBackClosedEmailsToEmployees
                    ? ""
                    : "opacity-50 cursor-not-allowed"
                }`}
              >
                <SettingsRow
                  id="email-sendForfeitureLeterToEmployees"
                  label="Allow Employees to receive forfeiture letter as attachment"
                  description="Attach forfeiture letters to the relevant buyback completion emails."
                  name="sendForfeitureLeterToEmployees"
                  type="toggle"
                  getFieldProps={getFieldProps}
                  value={values.sendForfeitureLeterToEmployees}
                  onToggle={() =>
                    formik.setFieldValue(
                      "sendForfeitureLeterToEmployees",
                      !values.sendForfeitureLeterToEmployees
                    )
                  }
                  disabled={
                    !values.sendEmailsToEmployees ||
                    !values.sendBuyBackClosedEmailsToEmployees
                  }
                />
              </VStack>

              <SettingsRow
                id="email-sendBuyBackReminderEmailsToEmployees"
                label="Reminder Emails"
                description="Send buyback-related reminder emails."
                name="sendBuyBackReminderEmailsToEmployees"
                type="toggle"
                getFieldProps={getFieldProps}
                value={values.sendBuyBackReminderEmailsToEmployees}
                onToggle={() =>
                  formik.setFieldValue(
                    "sendBuyBackReminderEmailsToEmployees",
                    !values.sendBuyBackReminderEmailsToEmployees
                  )
                }
                disabled={
                  !values.sendEmailsToEmployees ||
                  !values.sendBuyBackEmailsToEmployees
                }
              />
            </VStack>

            {/* Full and Final letter */}
            <SettingsRow
              id="email-sendFullAndFinalLetterToEmployees"
              label="Full and Final Letter Emails"
              description="Send a notification email to employees upon separation, requesting their acknowledgement and signature on the full and final settlement letter."
              name="sendFullAndFinalLetterToEmployees"
              type="toggle"
              getFieldProps={getFieldProps}
              value={values.sendFullAndFinalLetterToEmployees}
              onToggle={() =>
                formik.setFieldValue(
                  "sendFullAndFinalLetterToEmployees",
                  !values.sendFullAndFinalLetterToEmployees
                )
              }
              disabled={!values.sendEmailsToEmployees}
            />
          </VStack>
        </VStack>
      </HStack>
    </>
  );
};

export default EmailSettings;
